import { SetOptional } from 'type-fest';

import type { SUPPORTED_LOCALES } from 'constants/localization';
import type { PermissionStatementAction } from 'contexts/Permissions/constants';

export type StringifiedUUID = string;

export type APIList<T> = {
  object?: 'list';
  total_count: number;
  data: T[];
  next?: string;
  previous?: string;
  page?: number;
};

export type APIListRecord<T> = {
  object?: 'list';
  total_count: number;
  data: T;
  next?: string;
  previous?: string;
};

export type APIRecord = {
  object: 'record';
  data?: {
    [key: string]: any | null | undefined;
  };
  id?: string;
};
export type NewAPIRecord = SetOptional<APIRecord, 'id'>;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export type User = {
  object: 'user';
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_active?: boolean;
  is_organization_admin?: boolean;
  is_superuser?: boolean;
  timezone?: string;
  locale?: Locale | null;
  employee_ids?: string[];
  created_at: string;
  last_login: string;
  last_activity?: string;
  is_placeholder?: boolean;
  linked_employee?: Employee;
  verified_email?: boolean;
  role_id?: number | null;
  eligible_to_receive_inquiries?: boolean;
  eligible_to_submit_inquiries?: boolean;
  enable_inquiry_notifications?: boolean;
  enable_statement_notifications?: boolean;
};

export type Employee = {
  object: 'employee';
  id: string;
  eid: string;
  name?: string;
  first_name: string | null | undefined;
  last_name: string | null | undefined;
  email: string | null | undefined;
  last_process_date: string | null | undefined;
  is_active?: boolean;
  display_value?: string;
  linked_user?: User;
};

export type Organization = {
  id: string;
  name: string;
  created_at: string;
  organization_feature_flags: string[];
};

export type KeyProp = {
  key: StringifiedUUID;
};

export type PermissionStatement = {
  action: PermissionStatementAction;
  created_at?: string;
  updated_at?: string;
  id?: string;
  object?: 'permission_statement';
};

export type AccessorRole = {
  ciq_managed: boolean;
  organization: Organization | null;
  created_at: string;
  updated_at: string;
  id: string;
  name: string;
  object: 'accessor_role';
  description?: string;
  short_description?: string;
  permission_statements: PermissionStatement[];
  user_count: number;
};

export enum CollaboratorRoleAccessType {
  EDIT = 'edit',
  COLLABORATOR = 'collaborator',
  COMMENTER = 'commenter',
  APPROVER = 'approver',
}

export type TreeContextMenuApi = {
  openNodeId: string | null;
  parentBounds: DOMRect | null;
  treeBounds: DOMRect | null;
  openFor: (nodeId: string, anchorEl: HTMLElement) => void;
  close: () => void;
};

export type OpenAPIJobResponse = {
  object: 'job';
  id: StringifiedUUID;
  url: string;
  description: string;
  type: string;
  status: string;
  details: any; // Details will vary based on job type
  metadata: {
    user_id: StringifiedUUID;
    user_email: string;
    created_at: string;
    updated_at: string;
    aggregation_id: StringifiedUUID;
  };
};

export enum SourceType {
  DATA = 'data',
  TRANSFORMATION = 'transformation',
}

export type DeleteRequestData = {
  object: string;
  id: string;
};

export type UpdateRequestData = {
  object: string;
  id: string | undefined; // Unique ID of the modified row, undefined for new records
  data: Record<string, string | boolean | null>; // Map of column UUID => value
};

export type UpdateRecordsRequest<TMeta = void> = {
  object: string;
  updates: {
    object: string;
    data: UpdateRequestData[];
  };
  deletes: {
    object: string;
    data: DeleteRequestData[];
  };
  meta?: TMeta;
};
